import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete,
    Chip,
    FormControl,
    FormHelperText,
    FormLabel,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    SxProps,
    TextField,
    Typography,
} from '@mui/material';
import { bodyFont, headingFontBold, labelFontSemiBold, vars } from '../../../../../Theme';
import RadioBtnGroup from '../../_parts/RadioBtnGroup';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import { ICreateNoticeErrorState, ICreateNoticeMainState } from '../CreateNoticeDialog';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Icon from '@mdi/react';
import { mdiClose, mdiAlertCircle, mdiAccountGroup, mdiAccount } from '@mdi/js';
import { Dayjs } from 'dayjs';
import Message from '../../_parts/Message';
import { NoticeApprovalMethodEnum } from '../../../../../redux/project/documents/sliceProjectDocumentsCreateNotice';
import i18n from '../../../../../localization/i18n';
import {
    getProjectUserListWithoutCurrentUser,
    IDistributionGroup,
    IProjectUserContact,
    isDistributionGroup,
} from '../../../../../redux/project/sliceProjectUserList';
import { PickerChangeHandlerContext, DateValidationError } from '@mui/x-date-pickers';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiFormHelperText-root': {
        marginLeft: 'unset',
    },

    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '6.5px',
        pl: '14px',
    },
};

const chipSx: SxProps = {
    ...headingFontBold,
    backgroundColor: vars.tagChipBlue,
    color: vars.white,
    fontSize: '14px',
    fontWeight: 500,
    padding: '4px 6px',
    lineHeight: '120%',

    '.MuiChip-deleteIcon': {
        color: vars.white,
    },
};

export default ({
    mainState,
    handleChange,
    showErrorState,
    setShowErrorState,
}: ICreateNoticeContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'createNoticeDialog' });
    const approvalMethodOptions = [
        {
            key: NoticeApprovalMethodEnum.AtLeastOneNeedToApprove,
            label: t('atLeastOneNeedToApprove'),
        },
        {
            key: NoticeApprovalMethodEnum.AllNeedToApprove,
            label: t('allNeedToApprove'),
        },
    ];

    const { noticeTypes } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsCreateNotice,
    );

    const users = useSelector(getProjectUserListWithoutCurrentUser);
    const groups = useSelector((_store: IAppReduxStore) => _store.projectUserList.groups);

    const errorMessage = (
        <FormHelperText sx={{ marginLeft: '0px' }}>
            <Message
                text={t('errorInput')}
                icon={mdiAlertCircle}
                iconSize={0.75}
                color={vars.errorDialogRed}
                typographySx={{
                    fontWeight: 400,
                    fontSize: '12px',
                    ...bodyFont,
                }}
                wrapperSx={{
                    border: 'none',
                    p: 0,
                    m: 0,
                    gap: 0.5,
                }}
            />
        </FormHelperText>
    );

    return (
        <Stack spacing={'12px'}>
            <FormControl size='small' required>
                <FormLabel sx={formLabelSx}>{t('selectTaskType')}</FormLabel>
                <Select
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    value={mainState.taskType}
                    error={showErrorState.taskType}
                    onChange={(e: SelectChangeEvent<string>) => {
                        handleChange({
                            field: 'taskType',
                            value: e.target.value,
                        });
                    }}
                    renderValue={value =>
                        value || (
                            <Typography color={vars.placeHolderFont}>
                                {t('selectTaskTypePlaceholder')}
                            </Typography>
                        )
                    }
                >
                    {noticeTypes?.map(option => (
                        <MenuItem
                            sx={{
                                fontFamily: labelFontSemiBold,

                                '&.Mui-selected': {
                                    color: vars.brandGreen,
                                    backgroundColor: 'transparent',

                                    '&:hover': {
                                        color: vars.selectedHoveredGreen,
                                    },
                                },
                            }}
                            key={option[i18n.language] ? option[i18n.language] : option.en}
                            value={option[i18n.language] ? option[i18n.language] : option.en}
                        >
                            {option[i18n.language] ? option[i18n.language] : option.en}
                        </MenuItem>
                    ))}
                </Select>
                {showErrorState.taskType && errorMessage}
            </FormControl>

            <FormControl size='small'>
                <FormLabel sx={formLabelSx}>{t('selectDueDate')}</FormLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        disablePast
                        format='DD.MM.YYYY'
                        value={mainState.dueDate}
                        onChange={(
                            value: Dayjs,
                            context: PickerChangeHandlerContext<DateValidationError>,
                        ) => {
                            handleChange({ field: 'dueDate', value });

                            handleChange({ field: 'dueDateErrorContext', value: context });

                            setShowErrorState(prevState => ({
                                ...prevState,
                                dueDate: context.validationError !== null,
                            }));
                        }}
                        slotProps={{
                            field: {
                                clearable: true,
                                onClear: () => handleChange({ field: 'dueDate', value: null }),
                            },
                            textField: {
                                size: 'small',
                            },
                            inputAdornment: {
                                position: 'end',
                                style: { order: 2, marginLeft: 0 },
                            },
                        }}
                    />
                </LocalizationProvider>
                {showErrorState.dueDate && errorMessage}
            </FormControl>

            <FormControl size='small'>
                <FormLabel sx={formLabelSx}>{t('comment')}</FormLabel>
                <TextField
                    size='small'
                    sx={textFieldSx}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({ field: 'comment', value: e.target.value })
                    }
                    placeholder={t('enterComment')}
                    value={mainState.comment}
                    multiline
                />
            </FormControl>

            <FormControl size='small' required>
                <FormLabel sx={formLabelSx}>{t('selectAssignee')}</FormLabel>

                <Autocomplete
                    multiple
                    id='tags-standard'
                    options={[...users, ...groups].sort((a, b) => a.name.localeCompare(b.name))}
                    onChange={(_, newValue: (IProjectUserContact | IDistributionGroup)[]) => {
                        const users = newValue.filter(
                            value => !isDistributionGroup(value),
                        ) as IProjectUserContact[];
                        const groups = newValue.filter(value =>
                            isDistributionGroup(value),
                        ) as IDistributionGroup[];

                        const assignees: IProjectUserContact[] = [];
                        [...users, ...groups.flatMap(group => group.userList)].forEach(
                            (user: IProjectUserContact) => {
                                if (!assignees.some(x => x.notesName === user.notesName)) {
                                    assignees.push(user);
                                }
                            },
                        );

                        handleChange({ field: 'assignees', value: assignees });
                    }}
                    getOptionLabel={option => option.name}
                    value={mainState.assignees}
                    renderOption={(props, option) => {
                        return (
                            <ListItem
                                {...props}
                                sx={{
                                    fontFamily: labelFontSemiBold,

                                    color:
                                        mainState.assignees.find(
                                            assignee => assignee.name === option.name,
                                        ) && vars.brandGreen,

                                    '&:hover': {
                                        color:
                                            mainState.assignees.find(
                                                assignee => assignee.name === option.name,
                                            ) && vars.selectedHoveredGreen,
                                    },
                                }}
                                key={isDistributionGroup(option) ? option.name : option.notesName}
                            >
                                <ListItemIcon>
                                    <Icon
                                        path={
                                            isDistributionGroup(option)
                                                ? mdiAccountGroup
                                                : mdiAccount
                                        }
                                        color={vars.font}
                                        size={1}
                                    />
                                </ListItemIcon>
                                <ListItemText>{option.name}</ListItemText>
                            </ListItem>
                        );
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const tagProps = getTagProps({ index });
                            return (
                                <Chip
                                    {...tagProps}
                                    key={
                                        isDistributionGroup(option) ? option.name : option.notesName
                                    }
                                    variant='filled'
                                    label={option.name}
                                    size='small'
                                    sx={chipSx}
                                    deleteIcon={<Icon path={mdiClose} size={'16px'} />}
                                />
                            );
                        })
                    }
                    renderInput={params => (
                        <TextField
                            {...params}
                            sx={{
                                ...textFieldSx,
                                '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                                    pl: '6px',
                                },
                            }}
                            size='small'
                            placeholder={t('selectAssignee')}
                            error={showErrorState.assignees}
                        />
                    )}
                />
                {showErrorState.assignees && errorMessage}
            </FormControl>

            <FormControl size='small' required>
                <FormLabel sx={formLabelSx}>{t('approvalMethod')}</FormLabel>
                <RadioBtnGroup
                    value={mainState.approvalMethod}
                    onSelect={key => handleChange({ field: 'approvalMethod', value: key })}
                    options={approvalMethodOptions}
                    containerSx={{
                        m: 0,
                        marginBottom: showErrorState.approvalMethod ? '0px' : '16px',
                    }}
                    groupSx={{
                        border: showErrorState.approvalMethod
                            ? `1px solid ${vars.errorDialogRed}`
                            : `1px solid ${vars.lightGray}`,
                    }}
                    border={true}
                />
                {showErrorState.approvalMethod && errorMessage}
            </FormControl>
        </Stack>
    );
};

interface ICreateNoticeContentProps {
    mainState: ICreateNoticeMainState;
    handleChange: ({
        field,
        value,
    }: {
        field: string;
        value:
            | string
            | number
            | IProjectUserContact[]
            | Dayjs
            | PickerChangeHandlerContext<DateValidationError>;
    }) => void;
    showErrorState: ICreateNoticeErrorState;
    setShowErrorState: Dispatch<React.SetStateAction<ICreateNoticeErrorState>>;
}
